/**
 * Plain-language feature guide for Finance V2 dashboard.
 * Each item: what it is, what it does, how data flows, where to click.
 */

export const SETUP_CHECKLIST = [
  {
    step: 1,
    title: "Company & books start date",
    detail: "Name your company, currency (PKR), and the date you start counting books (go-live).",
    path: "/finance-v2/setup",
  },
  {
    step: 2,
    title: "Chart of accounts",
    detail: "Creates standard accounts: Cash, Bank, Sales, Expenses, Tax, Equity — like QuickBooks.",
    path: "/finance-v2/setup",
  },
  {
    step: 3,
    title: "Opening balances",
    detail: "Enter cash, bank, and loan balances as of go-live. System posts a balanced opening journal.",
    path: "/finance-v2/setup",
  },
  {
    step: 4,
    title: "Payment methods → accounts",
    detail: "Maps each checkout payment type (cash, card, bank transfer) to the right asset account.",
    path: "/finance-v2/setup",
  },
  {
    step: 5,
    title: "Package types → revenue",
    detail: "Maps Gym, PT, Cafe, etc. to income accounts so P&L splits by service.",
    path: "/finance-v2/setup",
  },
  {
    step: 6,
    title: "Expense categories → accounts",
    detail: "Maps old expense categories to expense accounts for legacy import.",
    path: "/finance-v2/setup",
  },
  {
    step: 7,
    title: "Launch Finance V2",
    detail: "Marks setup complete. New sales start posting journals automatically.",
    path: "/finance-v2/setup",
  },
];

export const QUICK_ACTIONS = [
  {
    id: "setup",
    icon: "bi-gear",
    title: "Setup wizard",
    short: "First-time configuration",
    path: "/finance-v2/setup",
    color: "secondary",
  },
  {
    id: "coa",
    icon: "bi-diagram-3",
    title: "Chart of accounts",
    short: "View & create accounts",
    path: "/finance-v2/chart-of-accounts",
    color: "primary",
  },
  {
    id: "journals",
    icon: "bi-journal-text",
    title: "Journal entries",
    short: "All posted transactions",
    path: "/finance-v2/journals",
    color: "primary",
  },
  {
    id: "transactions",
    icon: "bi-pencil-square",
    title: "Enter transactions",
    short: "Expenses, transfers, charity, liabilities",
    path: "/finance-v2/transactions",
    color: "info",
  },
  {
    id: "reports",
    icon: "bi-bar-chart-line",
    title: "Financial reports",
    short: "P&L, balance sheet, trial balance",
    path: "/finance-v2/reports",
    color: "success",
  },
  {
    id: "gl",
    icon: "bi-list-columns",
    title: "General ledger",
    short: "Account history + running balance",
    path: "/finance-v2/general-ledger",
    color: "success",
  },
  {
    id: "bank",
    icon: "bi-bank",
    title: "Bank reconciliation",
    short: "Match bank statement to books",
    path: "/finance-v2/bank-reconcile",
    color: "warning",
  },
  {
    id: "ar",
    icon: "bi-people",
    title: "Receivables (A/R)",
    short: "Who owes money on orders",
    path: "/finance-v2/receivables",
    color: "warning",
  },
  {
    id: "periods",
    icon: "bi-calendar-check",
    title: "Close period",
    short: "Lock a month from new entries",
    path: "/finance-v2/fiscal-periods",
    color: "dark",
  },
  {
    id: "import",
    icon: "bi-clock-history",
    title: "Import history",
    short: "One-time pull from old data (read)",
    path: "/finance-v2/import",
    color: "outline-dark",
  },
];

export const FEATURE_CATEGORIES = [
  {
    id: "foundation",
    title: "Foundation — how the books work",
    intro:
      "Finance V2 uses double-entry accounting (like QuickBooks). Every transaction has equal debits and credits so books always balance.",
    features: [
      {
        name: "Double-entry journals",
        what: "Each sale, expense, or transfer creates a journal with 2+ lines (debit and credit).",
        how: "View under Journals. Manual entries allowed for adjustments.",
        auto: "Posted automatically when you complete setup.",
        link: "/finance-v2/journals",
      },
      {
        name: "Chart of accounts (COA)",
        what: "List of all accounts: assets, liabilities, equity, income, expenses.",
        how: "Seed standard chart in setup, or add custom accounts anytime.",
        auto: "One-time seed + manual create",
        link: "/finance-v2/chart-of-accounts",
      },
      {
        name: "Opening balances",
        what: "Starting cash, bank, and equity balances on your go-live date.",
        how: "Entered in setup step 3; posts journal OB-YYYY-MM-DD.",
        auto: "Once during setup",
        link: "/finance-v2/setup",
      },
      {
        name: "Integration mappings",
        what: "Rules that tell the system which account to use for payments, packages, expenses.",
        how: "Configured in setup wizard steps 4–6.",
        auto: "Applied on every sync and new transaction",
        link: "/finance-v2/setup",
      },
    ],
  },
  {
    id: "sales",
    title: "Sales & checkout (linked to orders)",
    intro:
      "When a customer pays for a package, Finance V2 builds a journal: money to Cash/Bank, revenue to income accounts, tax if applicable.",
    features: [
      {
        name: "Auto-sync on new order",
        what: "Every completed order posts a journal without manual work.",
        how: "Runs after order save in the POS/checkout flow.",
        auto: "Yes — real-time after setup",
        link: "/finance-v2/journals",
      },
      {
        name: "FBR invoice on journal",
        what: "FBR invoice number from the order appears in the journal description.",
        how: "Pulled from order data when syncing.",
        auto: "Yes",
        link: null,
      },
      {
        name: "Payment method mapping",
        what: "Cash vs card vs bank goes to the correct asset account.",
        how: "Setup step 4 — map each payment method.",
        auto: "On each sale",
        link: "/finance-v2/setup",
      },
      {
        name: "Revenue by package category",
        what: "Gym vs PT vs Cafe revenue hits different income accounts.",
        how: "Setup step 5 — map package categories.",
        auto: "On each sale",
        link: "/finance-v2/setup",
      },
      {
        name: "Historical sales backfill",
        what: "Import old orders into journals in batches.",
        how: "Dashboard → Import sales — batch 1000 or Process all.",
        auto: "Manual trigger (one-time or catch-up)",
        link: "/finance-v2/dashboard",
      },
    ],
  },
  {
    id: "expenses",
    title: "Expenses, transfers & charity (enter in V2 only)",
    intro:
      "All new spending and cash movements are entered in Finance V2. Legacy screens are history only — do not add new rows there.",
    features: [
      {
        name: "New expense entry",
        what: "Debit expense head, credit cash/bank — live books.",
        how: "Finance → Enter transactions → Expense. Pick exact head (Media, Rent, etc.) and paid-from account.",
        auto: "Manual entry in V2",
        link: "/finance-v2/transactions",
      },
      {
        name: "Transfer between accounts",
        what: "Move money Cash ↔ Bank ↔ Petty (replaces old bank/petty add screens).",
        how: "Enter transactions → Transfer tab.",
        auto: "Manual entry in V2",
        link: "/finance-v2/transactions",
      },
      {
        name: "Charity entries",
        what: "Charity paid out or received — posts to charity expense/income heads.",
        how: "Enter transactions → Charity tab.",
        auto: "Manual entry in V2",
        link: "/finance-v2/transactions",
      },
      {
        name: "Pay liability",
        what: "Reduce a loan/A/P by paying from cash or bank.",
        how: "Enter transactions → Pay liability.",
        auto: "Manual entry in V2",
        link: "/finance-v2/transactions",
      },
      {
        name: "Historical import only",
        what: "One-time pull of old expense rows into journals — not for daily entry.",
        how: "Import & sync center, or History import tab.",
        auto: "Manual import",
        link: "/finance-v2/import",
      },
      {
        name: "Vostro daily expense import",
        what: "Import historical vostro_expense headers (past data only).",
        how: "Import center → Vostro expenses.",
        auto: "Manual import",
        link: "/finance-v2/import",
      },
    ],
  },
  {
    id: "hr",
    title: "Payroll & HR (users_finance)",
    intro: "Salary and payroll-related amounts from HR can be posted as expense journals.",
    features: [
      {
        name: "Payroll import & auto-sync",
        what: "Creates journals from users_finance records (salary components).",
        how: "Auto-posts on new HR payroll save; use Import payroll for historical rows.",
        auto: "Yes — real-time after setup + manual backfill",
        link: "/finance-v2/import",
      },
    ],
  },
  {
    id: "reports",
    title: "Reports & analysis",
    intro: "Standard accounting reports built from posted journals — same idea as QuickBooks reports.",
    features: [
      {
        name: "Profit & Loss (P&L)",
        what: "Income minus expenses for a date range.",
        how: "Reports → P&L tab. Uses mapped revenue and expense accounts.",
        auto: "Live from posted journals",
        link: "/finance-v2/reports",
      },
      {
        name: "Balance sheet",
        what: "Assets, liabilities, and equity as of a date.",
        how: "Reports → Balance sheet tab.",
        auto: "Live",
        link: "/finance-v2/reports",
      },
      {
        name: "Trial balance",
        what: "Every account’s debit and credit totals — must balance.",
        how: "Reports → Trial balance tab.",
        auto: "Live",
        link: "/finance-v2/reports",
      },
      {
        name: "General ledger",
        what: "All lines for one account with running balance.",
        how: "General ledger screen — pick account and dates.",
        auto: "Live",
        link: "/finance-v2/general-ledger",
      },
      {
        name: "Dashboard metrics",
        what: "Month-to-date revenue, expenses, net income, sync count.",
        how: "This page — top cards.",
        auto: "Live",
        link: "/finance-v2/dashboard",
      },
    ],
  },
  {
    id: "operations",
    title: "Operations — bank, A/R, period close",
    intro: "Month-end and collection tools finance managers use after daily posting.",
    features: [
      {
        name: "Bank reconciliation",
        what: "Compare book balance to bank statement; mark cleared lines.",
        how: "Select bank account → load uncleared → tick cleared items.",
        auto: "Manual reconcile",
        link: "/finance-v2/bank-reconcile",
      },
      {
        name: "Accounts receivable aging",
        what: "Shows pending payment amounts on orders by age bucket.",
        how: "From payments_history.pending — not a separate AR ledger yet.",
        auto: "Live read from orders",
        link: "/finance-v2/receivables",
      },
      {
        name: "Close accounting period",
        what: "Locks a month so new journals cannot post into closed dates.",
        how: "Fiscal periods → Close on the period row.",
        auto: "Manual close",
        link: "/finance-v2/fiscal-periods",
      },
      {
        name: "Void journal",
        what: "Cancel a posted journal (keeps audit trail).",
        how: "Journals list → expand lines or Void action on a posted entry.",
        auto: "Manual",
        link: "/finance-v2/journals",
      },
    ],
  },
  {
    id: "legacy",
    title: "Legacy finance (history / read only)",
    intro:
      "Old screens remain only so you can look up past entries. Do not enter new expenses, charity, bank, or petty cash there — use Finance V2 Enter transactions.",
    features: [
      {
        name: "View old ledgers",
        what: "Historical expense / bank / charity lists for reference.",
        how: "Sidebar → Legacy history (read only).",
        auto: "Read only",
        link: "/view-expenses",
      },
      {
        name: "Import past data into V2",
        what: "One-time sync of old rows into V2 journals.",
        how: "Finance V2 → Import history.",
        auto: "Manual import",
        link: "/finance-v2/import",
      },
    ],
  },
];

export const DAILY_WORKFLOW = [
  {
    when: "First time only",
    tasks: [
      "Complete setup wizard (already done for F-11)",
      "Confirm chart of accounts has all expense heads",
      "Import any remaining history via Import center (optional)",
      "Open Trial balance and confirm debits = credits",
    ],
  },
  {
    when: "Every day",
    tasks: [
      "Sales post automatically from POS — check journals if needed",
      "Enter every expense / transfer / charity / liability payment in Finance V2 → Enter transactions",
      "Do not use Legacy Add screens",
      "Review P&L and General ledger for the day",
    ],
  },
  {
    when: "Month end",
    tasks: [
      "Run bank reconciliation for each bank account",
      "Review P&L and balance sheet in Finance V2 Reports",
      "Close the fiscal period when numbers are final",
    ],
  },
];

export const NOT_INCLUDED = [
  "Full QuickBooks Online clone (inventory, payroll tax filings, QBO bank feeds, 1099)",
  "Automatic reversing journal when an order is refunded (void on delete is supported)",
  "Multi-currency revaluation and consolidated multi-branch reporting",
  "Dedicated AP bills workflow (vendors exist; use expense + A/P flag for now)",
];
